import { useEffect, useState } from 'react';

/**
 * The light/dark choice, held in one place.
 *
 * It used to live inside ThemeToggle, which was fine while exactly one toggle
 * was on screen. It is now offered from the account menu as well as from the
 * contractor portal's own header, and two components each holding their own
 * copy of the answer is how a switch ends up showing "Dark" while the page is
 * light: both write the same attribute, neither hears the other.
 *
 * So the state is module-level and the components subscribe. Three theme
 * states, not two: an explicit light choice, an explicit dark choice, and -
 * the default - no choice at all, where the OS decides and we keep following
 * it live. Only the first two stamp data-theme.
 */
const STORAGE_KEY = 'jarvis-theme';
const canMatch = typeof window !== 'undefined' && typeof window.matchMedia === 'function';

function systemTheme() {
  return canMatch && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function storedTheme() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'light' || v === 'dark' ? v : null;
  } catch (err) {
    // Private windows and blocked site data both throw here.
    return null;
  }
}

let theme = storedTheme() || systemTheme();
let explicit = storedTheme() !== null;
const listeners = new Set();

function apply() {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', theme);
  }
}

apply();

function set(next, remember) {
  if (next !== 'light' && next !== 'dark') return;
  theme = next;
  if (remember) {
    explicit = true;
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch (err) {
      // Not being able to remember it is survivable; switching still works.
    }
  }
  apply();
  listeners.forEach((fn) => fn(theme));
}

// Keep following the OS until the user makes a choice of their own. Attached
// once, for the life of the page, rather than per component.
if (canMatch) {
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  const onChange = (e) => { if (!explicit) set(e.matches ? 'dark' : 'light', false); };
  if (typeof mq.addEventListener === 'function') mq.addEventListener('change', onChange);
  else if (typeof mq.addListener === 'function') mq.addListener(onChange);
}

export function currentTheme() { return theme; }

export function toggleTheme() { set(theme === 'dark' ? 'light' : 'dark', true); }

export function useTheme() {
  const [value, setValue] = useState(theme);
  useEffect(() => {
    listeners.add(setValue);
    // The module may have changed its mind between render and effect.
    setValue(theme);
    return () => { listeners.delete(setValue); };
  }, []);
  return { theme: value, dark: value === 'dark', toggle: toggleTheme };
}
